// Reads from localStorage via useRecentlyViewed hook.
// No API call needed — all data was stored when the user visited the product.
// ─────────────────────────────────────────────────────────────────────────────

// import { useRef, useState, useEffect } from "react";
// import { useNavigate }                 from "react-router-dom";
// import { ChevronLeft, ChevronRight, Clock, X } from "lucide-react";
// import { useRecentlyViewed }           from "../../hooks/useRecentlyViewed";  // ← adjust path
// import { getLanguage }                 from "../../utils/Helper";
// import styles                          from "./RecentlyViewed.module.scss";

// // ─── i18n ────────────────────────────────────────────────────────────────────
// const t = {
//   EN: {
//     tag:     "Your History",
//     title:   "Recently Viewed",
//     clear:   "Clear all",
//     empty:   "You haven't viewed any products yet.",
//     sale:    "SALE",
//   },
//   AR: {
//     tag:     "سجلّك",
//     title:   "شفتها مؤخراً",
//     clear:   "مسح الكل",
//     empty:   "لم تشاهد أي منتجات بعد.",
//     sale:    "خصم",
//   },
// };

// // ─── helpers ─────────────────────────────────────────────────────────────────
// const slug = (s) =>
//   s?.toString().toLowerCase().trim().split(" ").join("-") ?? "";

// const calcDiscount = (price, orig) =>
//   price && orig && price < orig
//     ? Math.round(((orig - price) / orig) * 100)
//     : null;

// // ─── ProductCard ──────────────────────────────────────────────────────────────
// const ProductCard = ({ product, lang, index }) => {
//   const [imgError, setImgError] = useState(false);
//   const navigate = useNavigate();
//   const i18n     = t[lang] ?? t.EN;
//   const isRtl    = lang === "AR";

//   const name     = lang === "AR" ? (product.nameAr || product.nameEn) : product.nameEn;
//   const discount = calcDiscount(product.price, product.originalPrice);

//   const goTo = () => {
//     const path = `/collection/${slug(product.collectionNameEn)}/${slug(product.categoryNameEn)}/${slug(product.subCategoryNameEn)}/${product.id}`;
//     navigate(path);
//   };

//   return (
//     <article
//       className={styles.card}
//       style={{ animationDelay: `${index * 0.05}s` }}
//       onClick={goTo}
//       dir={isRtl ? "rtl" : "ltr"}
//     >
//       {/* Image */}
//       <div className={styles.imgWrap}>
//         {!imgError && product.primaryImageUrl ? (
//           <img
//             src={product.primaryImageUrl}
//             alt={name}
//             className={styles.img}
//             loading="lazy"
//             onError={() => setImgError(true)}
//           />
//         ) : (
//           <div className={styles.imgPlaceholder}>🛍️</div>
//         )}

//         {/* Sale badge */}
//         {discount && (
//           <span className={styles.saleBadge}>-{discount}%</span>
//         )}

//         {/* Hover overlay */}
//         <div className={styles.overlay}>
//           <span className={styles.overlayTxt}>
//             {lang === "AR" ? "عرض المنتج" : "View product"}
//           </span>
//         </div>
//       </div>

//       {/* Info */}
//       <div className={styles.info}>
//         <h3 className={styles.name}>{name}</h3>

//         <div className={styles.priceRow}>
//           {product.price && (
//             <span className={styles.priceNow}>
//               {product.price.toLocaleString("en-EG")} EGP
//             </span>
//           )}
//           {discount && product.originalPrice && (
//             <span className={styles.priceWas}>
//               {product.originalPrice.toLocaleString("en-EG")} EGP
//             </span>
//           )}
//         </div>
//       </div>
//     </article>
//   );
// };

// // ─── Main component ───────────────────────────────────────────────────────────
// const RecentlyViewed = ({ excludeId = null }) => {
//   const lang  = getLanguage();
//   const i18n  = t[lang] ?? t.EN;
//   const isRtl = lang === "AR";

//   const { items, clearAll } = useRecentlyViewed();

//   // filter out current product so it doesn't show in its own page section
//   const products = items.filter((p) => p.id !== excludeId);

//   // ── carousel scroll (same pattern as AmazonNavbar) ────────────────────────
//   const [canScrollLeft,  setCanScrollLeft]  = useState(false);
//   const [canScrollRight, setCanScrollRight] = useState(false);
//   const trackRef = useRef(null);

//   const checkScroll = () => {
//     const el = trackRef.current;
//     if (!el) return;
//     setCanScrollLeft(el.scrollLeft > 4);
//     setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
//   };

//   const scrollBy = (dir) =>
//     trackRef.current?.scrollBy({ left: dir === "right" ? 260 : -260, behavior: "smooth" });

//   useEffect(() => {
//     const el = trackRef.current;
//     if (!el) return;
//     checkScroll();
//     el.addEventListener("scroll", checkScroll, { passive: true });
//     const ro = new ResizeObserver(checkScroll);
//     ro.observe(el);
//     return () => { el.removeEventListener("scroll", checkScroll); ro.disconnect(); };
//   }, [products.length]);

//   // nothing to show
//   if (products.length === 0) return null;

//   return (
//     <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
//       <div className={styles.inner}>

//         {/* ── Header ── */}
//         <div className={styles.header}>
//           <div className={styles.headerLeft}>
//             <div className={styles.tagRow}>
//               <Clock size={13} className={styles.clockIcon} />
//               <span className={styles.tag}>{i18n.tag}</span>
//             </div>
//             <h2 className={styles.title}>{i18n.title}</h2>
//           </div>

//           <div className={styles.headerRight}>
//             {/* Clear button */}
//             <button className={styles.clearBtn} onClick={clearAll}>
//               <X size={13} />
//               {i18n.clear}
//             </button>

//             {/* Arrow buttons */}
//             <div className={styles.arrowGroup}>
//               <button
//                 className={`${styles.arrowBtn} ${!canScrollLeft ? styles.arrowDisabled : ""}`}
//                 onClick={() => scrollBy("left")}
//                 aria-label="scroll left"
//               >
//                 <ChevronLeft size={17} />
//               </button>
//               <button
//                 className={`${styles.arrowBtn} ${!canScrollRight ? styles.arrowDisabled : ""}`}
//                 onClick={() => scrollBy("right")}
//                 aria-label="scroll right"
//               >
//                 <ChevronRight size={17} />
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* ── Carousel track ── */}
//         <div className={styles.scrollWrap}>
//           {/* Fade masks */}
//           <div className={`${styles.fade} ${styles.fadeLeft}  ${canScrollLeft  ? styles.fadeOn : ""}`} />
//           <div className={`${styles.fade} ${styles.fadeRight} ${canScrollRight ? styles.fadeOn : ""}`} />

//           <div
//             className={styles.track}
//             ref={trackRef}
//             onScroll={checkScroll}
//           >
//             {products.map((product, i) => (
//               <div key={product.id} className={styles.slot}>
//                 <ProductCard product={product} lang={lang} index={i} />
//               </div>
//             ))}
//           </div>
//         </div>

//       </div>
//     </section>
//   );
// };

// export default RecentlyViewed;

 
import { useRef, useState, useEffect } from "react";
import { useNavigate }                 from "react-router-dom";
import { ChevronLeft, ChevronRight, Clock, X } from "lucide-react";
import { useRecentlyViewed }           from "../../hooks/useRecentlyViewed";  // ← adjust path
import { getLanguage }                 from "../../utils/Helper";
import styles                          from "./RecentlyViewed.module.scss";
 
// ─── i18n ────────────────────────────────────────────────────────────────────
const t = {
  EN: {
    tag:     "Your History",
    title:   "Recently Viewed",
    // clear:   "Clear all",
    empty:   "You haven't viewed any products yet.",
    sale:    "SALE",
  },
  AR: {
    tag:     "سجلّك",
    title:   "شفتها مؤخراً",
    // clear:   "مسح الكل",
    empty:   "لم تشاهد أي منتجات بعد.",
    sale:    "خصم",
  },
};
 
// ─── helpers ─────────────────────────────────────────────────────────────────
const slug = (s) =>
  s?.toString().toLowerCase().trim().split(" ").join("-") ?? "";
 
const calcDiscount = (price, orig) =>
  price && orig && price < orig
    ? Math.round(((orig - price) / orig) * 100)
    : null;
 
// ─── ProductCard ──────────────────────────────────────────────────────────────
const ProductCard = ({ product, lang, index }) => {
  const [imgError, setImgError] = useState(false);
  const navigate = useNavigate();
  const i18n     = t[lang] ?? t.EN;
  const isRtl    = lang === "AR";
 
  const name     = lang === "AR" ? (product.nameAr || product.nameEn) : product.nameEn;
  const discount = calcDiscount(product.price, product.originalPrice);
 
  const goTo = () => {
    const path = `/collection/${slug(product.collectionNameEn)}/${slug(product.categoryNameEn)}/${slug(product.subCategoryNameEn)}/${product.id}`;
    navigate(path);
  };
 
  return (
    <article
      className={styles.card}
      style={{ animationDelay: `${index * 0.05}s` }}
      onClick={goTo}
      dir={isRtl ? "rtl" : "ltr"}
    >
      {/* Image */}
      <div className={styles.imgWrap}>
        {!imgError && product.primaryImageUrl ? (
          <img
            src={product.primaryImageUrl}
            alt={name}
            className={styles.img}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={styles.imgPlaceholder}>🛍️</div>
        )}
 
        {/* Sale badge */}
        {discount && (
          <span className={styles.saleBadge}>-{discount}%</span>
        )}
 
        {/* Hover overlay */}
        <div className={styles.overlay}>
          <span className={styles.overlayTxt}>
            {lang === "AR" ? "عرض المنتج" : "View product"}
          </span>
        </div>
      </div>
 
      {/* Info */}
      <div className={styles.info}>
        <h3 className={styles.name}>{name}</h3>
 
        <div className={styles.priceRow}>
          {product.price && (
            <span className={styles.priceNow}>
              {product.price.toLocaleString("en-EG")} EGP
            </span>
          )}
          {discount && product.originalPrice && (
            <span className={styles.priceWas}>
              {product.originalPrice.toLocaleString("en-EG")} EGP
            </span>
          )}
        </div>
      </div>
    </article>
  );
};
 
// ─── Main component ───────────────────────────────────────────────────────────
const RecentlyViewed = ({ excludeId = null }) => {
  const lang  = getLanguage();
  const i18n  = t[lang] ?? t.EN;
  const isRtl = lang === "AR";
 
  const { items, clearAll } = useRecentlyViewed();
 
  // filter out current product so it doesn't show in its own page section
  const products = items.filter((p) => p.id !== excludeId);
 
  // ── carousel scroll (same pattern as AmazonNavbar) ────────────────────────
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const trackRef = useRef(null);
 
  const checkScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };
 
  const scrollBy = (dir) =>
    trackRef.current?.scrollBy({ left: dir === "right" ? 260 : -260, behavior: "smooth" });
 
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", checkScroll); ro.disconnect(); };
  }, [products.length]);
 
  // nothing to show
  if (products.length === 0) return null;
 
  return (
    <section className={styles.section} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.inner}>
 
        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.tagRow}>
              <Clock size={13} className={styles.clockIcon} />
              <span className={styles.tag}>{i18n.tag}</span>
            </div>
            <div className={styles.titleWrap}>
              <h2 className={styles.title}>{i18n.title}</h2>
              <span className={styles.titleUnderline} />
            </div>
          </div>
 
          <div className={styles.headerRight}>
            {/* Clear button */}
            {/* <button className={styles.clearBtn} onClick={clearAll}>
              <X size={13} />
              {i18n.clear}
            </button> */}
 
            {/* Arrow buttons */}
            <div className={styles.arrowGroup}>
              <button
                className={`${styles.arrowBtn} ${!canScrollLeft ? styles.arrowDisabled : ""}`}
                onClick={() => scrollBy("left")}
                aria-label="scroll left"
              >
                <ChevronLeft size={17} />
              </button>
              <button
                className={`${styles.arrowBtn} ${!canScrollRight ? styles.arrowDisabled : ""}`}
                onClick={() => scrollBy("right")}
                aria-label="scroll right"
              >
                <ChevronRight size={17} />
              </button>
            </div>
          </div>
        </div>
 
        {/* ── Carousel track ── */}
        <div className={styles.scrollWrap}>
          {/* Fade masks */}
          <div className={`${styles.fade} ${styles.fadeLeft}  ${canScrollLeft  ? styles.fadeOn : ""}`} />
          <div className={`${styles.fade} ${styles.fadeRight} ${canScrollRight ? styles.fadeOn : ""}`} />
 
          <div
            className={styles.track}
            ref={trackRef}
            onScroll={checkScroll}
          >
            {products.map((product, i) => (
              <div key={product.id} className={styles.slot}>
                <ProductCard product={product} lang={lang} index={i} />
              </div>
            ))}
          </div>
        </div>
 
      </div>
    </section>
  );
};
 
export default RecentlyViewed;